import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import NewRide from "@/pages/NewRide";
import RidesList from "@/pages/RidesList";
import GroupsList from "@/pages/GroupsList";
import Calendar from "@/pages/Calendar";
import PlacesList from "@/pages/PlacesList";
import EditRide from "@/pages/EditRide";
import RideDetail from "@/pages/RideDetail";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of session_id in URL fragment — must run BEFORE
  // any ProtectedRoute check, otherwise the auth race condition kicks in.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute requireGroup={true}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={<Home />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/rides" element={<RidesList />} />
        <Route path="/groups" element={<GroupsList />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/places" element={<PlacesList />} />
        <Route path="/rides/new" element={<NewRide />} />
        <Route path="/rides/:id/edit" element={<EditRide />} />
        <Route path="/rides/:id" element={<RideDetail />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return null;
  const search = location.search || "";
  const params = new URLSearchParams(search);
  const invite = params.get("invite");
  if (!user) {
    if (invite) return <Navigate to={`/register?invite=${encodeURIComponent(invite)}`} replace />;
    return <Navigate to="/login" replace />;
  }
  if (invite) return <Navigate to={`/onboarding?invite=${encodeURIComponent(invite)}`} replace />;
  if (!(user.active_group_id || user.group_id)) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/home" replace />;
}

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
